import React from "react";
import { useLogoUrl } from "@/components/templates/useLogo";

/* Toont tekst met {{ placeholders }} en {% jinja %} als visuele chips */
export function TekstMetChips({ tekst }) {
  const delen = String(tekst || "").split(/({{.*?}}|{%.*?%})/g);
  return (
    <>
      {delen.map((d, i) => {
        if (/^{{.*}}$/.test(d)) {
          const tag = d.replace(/^{{r?\s*/, "").replace(/\s*}}$/, "");
          return (
            <span key={i} className="inline-block bg-primary/10 text-primary rounded px-1 py-0 text-[10px] font-mono align-baseline">
              {tag}
            </span>
          );
        }
        if (/^{%.*%}$/.test(d)) {
          return (
            <span key={i} className="inline-block bg-amber-100 text-amber-700 rounded px-1 py-0 text-[10px] font-mono align-baseline">
              {d.replace(/[{}%]/g, "").trim()}
            </span>
          );
        }
        return <span key={i}>{d}</span>;
      })}
    </>
  );
}

const ALIGN_CLS = { links: "text-left", midden: "text-center", rechts: "text-right" };

function KvRegel({ label, tag }) {
  return (
    <div className="text-[11px]">
      {label ? <span className="font-semibold">{label}: </span> : null}
      <span className="inline-block bg-primary/10 text-primary rounded px-1 text-[10px] font-mono">{tag || "—"}</span>
    </div>
  );
}

const ALIGN_JUSTIFY = { links: "justify-start", midden: "justify-center", rechts: "justify-end" };

function LogoWeergave({ blok }) {
  const logoUrl = useLogoUrl();
  // A4 is 210 mm breed; preview-paneel ≈ 560 px → ± 2.6 px per mm
  const breedte = Math.min(Number(blok.breedte_mm) || 46, 180) * 2.6;
  return (
    <div className={`flex ${ALIGN_JUSTIFY[blok.uitlijning] || "justify-start"}`}>
      {logoUrl ? <img src={logoUrl} alt="Logo" style={{ width: `${breedte}px` }} className="object-contain" />
        : <span className="text-[10px] text-muted-foreground border border-dashed border-border rounded px-2 py-1">Logo</span>}
    </div>
  );
}

function BlokWeergave({ blok }) {
  const t = blok.type;
  if (t === "logo") return <LogoWeergave blok={blok} />;
  if (t === "kop") {
    const px = Number(blok.grootte_px) || 29;
    const stijl = { fontSize: `${px}px`, lineHeight: 1.2, fontWeight: 700 };
    return <div className={`${ALIGN_CLS[blok.uitlijning] || ""}`} style={stijl}><TekstMetChips tekst={blok.tekst} /></div>;
  }
  if (t === "tekst") {
    return (
      <div className={`text-[11px] whitespace-pre-wrap ${ALIGN_CLS[blok.uitlijning] || ""} ${blok.vet ? "font-semibold" : ""}`}>
        <TekstMetChips tekst={blok.tekst} />
      </div>
    );
  }
  if (t === "velden") {
    return <div className="space-y-0.5">{(blok.items || []).map((it, i) => <KvRegel key={i} label={it.label} tag={it.tag} />)}</div>;
  }
  if (t === "kolommen") {
    return (
      <div className="flex gap-3">
        {(blok.kolommen || []).map((kol, i) => (
          <div key={i} className="flex-1 min-w-0">
            {kol.titel ? <div className="text-[11px] font-bold mb-0.5">{kol.titel}</div> : null}
            {kol.inhoud === "adres" ? (
              <span className="inline-block bg-primary/10 text-primary rounded px-1 text-[10px] font-mono">{kol.adres_tag}</span>
            ) : (
              <div className="space-y-0.5">{(kol.velden || []).map((it, j) => <KvRegel key={j} label={it.label} tag={it.tag} />)}</div>
            )}
          </div>
        ))}
      </div>
    );
  }
  if (t === "regeltabel") {
    const kolommen = blok.kolommen || [];
    const randen = blok.randen || "alle";
    const kleur = blok.rand_kleur || "#000000";
    const celStijl = randen === "alle" ? { border: `1px solid ${kleur}` }
      : randen === "horizontaal" ? { borderBottom: `1px solid ${kleur}` } : {};
    const kopStijl = { ...celStijl, ...(blok.kop_achtergrond ? { backgroundColor: blok.kop_achtergrond } : {}) };
    // Bereken relatieve breedtes: cm-waardes uit editor + defaults voor ontbrekende.
    const totBreedte = 16.5;
    const totCm = kolommen.reduce((s, k) => s + (Number(k.breedte_cm) || 0), 0);
    const auto = kolommen.filter((k) => !k.breedte_cm).length;
    const restCm = Math.max(2, totBreedte - totCm);
    const autoCm = auto ? restCm / auto : 0;
    const kolBreedtes = kolommen.map((k) => Number(k.breedte_cm) || autoCm);
    return (
      <table className="w-full border-collapse" style={{ tableLayout: "fixed" }}>
        <colgroup>
          {kolBreedtes.map((cm, i) => <col key={i} style={{ width: `${(cm / totBreedte) * 100}%` }} />)}
        </colgroup>
        <thead>
          <tr>{kolommen.map((k, i) => <th key={i} style={kopStijl} className="px-1.5 py-1 text-left text-[10px] font-bold truncate">{k.label}</th>)}</tr>
        </thead>
        <tbody>
          <tr>{kolommen.map((k, i) => (
            <td key={i} style={celStijl} className="px-1.5 py-1 truncate">
              <span className="inline-block bg-primary/10 text-primary rounded px-1 text-[10px] font-mono">r.{k.key}</span>
            </td>
          ))}</tr>
        </tbody>
      </table>
    );
  }
  if (t === "totalen") {
    const cls = ALIGN_CLS[blok.uitlijning ?? "rechts"] || "text-right";
    return (
      <div className={`space-y-0.5 ${cls}`}>
        {(blok.items || []).map((it, i) => <KvRegel key={i} label={it.label} tag={it.tag} />)}
      </div>
    );
  }
  if (t === "spacer") return <div className="h-4" />;
  if (t === "footer") {
    return (
      <div className="border-t border-border pt-1.5 text-[10px] text-muted-foreground space-y-0.5">
        {(blok.regels || []).map((r, i) => <div key={i}><TekstMetChips tekst={r} /></div>)}
      </div>
    );
  }
  return null;
}

export function BlokPreview({ blokken, lettertype }) {
  const stijl = lettertype ? { fontFamily: `'${lettertype}', sans-serif` } : undefined;
  // Logo staat áltijd in de header van het Word-document, dus we tonen het bovenaan
  // (los van de plek waar het 'logo'-blok in de blokkenlijst staat). Als er geen logo-blok
  // is, tonen we alsnog een default header met het bedrijfslogo.
  const logoBlok = (blokken || []).find((b) => b.type === "logo") || { type: "logo", breedte_mm: 46, uitlijning: "rechts" };
  const bodyBlokken = (blokken || []).filter((b) => b.type !== "logo");
  return (
    <div className="bg-white text-black rounded-lg border border-border shadow-sm p-6 min-h-[500px] space-y-3"
      style={stijl} data-testid="template-preview">
      <div className="border-b border-dashed border-border/60 pb-3 mb-2" data-testid="template-preview-header">
        <div className="text-[9px] uppercase tracking-wide text-muted-foreground mb-1">Header (elke pagina)</div>
        <LogoWeergave blok={logoBlok} />
      </div>
      {bodyBlokken.length === 0 && (
        <div className="text-xs text-muted-foreground">Nog geen inhoudsblokken — voeg links een blok toe.</div>
      )}
      {bodyBlokken.map((b) => <BlokWeergave key={b._key} blok={b} />)}
    </div>
  );
}
